import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { setActiveWorkspace } from '../../lib/workspace'
import { pickCurrent, useWorkspaces } from './useWorkspaces'
import { BUSINESS_CSS } from './businessStyles'

/**
 * Business switcher at the top of the app rail.
 *
 * Shows the current business (its first letter in a square badge when the
 * rail is collapsed) and a menu to switch, open the Businesses page, or start
 * a new business. Renders nothing until the list has loaded, and nothing at
 * all if loading fails, so it can never take the rail down with it.
 *
 * The menu is `position: fixed`, placed from the trigger's rectangle. The
 * rail scrolls on overflow, which would clip an absolutely positioned menu
 * wider than the collapsed 56px rail.
 */

interface Props {
  /** Pass the rail's collapsed state. Falls back to `body.rail-collapsed`. */
  collapsed?: boolean
}

function isRailCollapsed(): boolean {
  return typeof document !== 'undefined' && document.body.classList.contains('rail-collapsed')
}

function openBusinessesPage(): void {
  window.openBusinessesPage?.()
}

export default function BusinessSwitcher({ collapsed }: Props) {
  const { workspaces, status } = useWorkspaces()
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }, [])

  // Click outside and Escape close the menu.
  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') close(true)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, close])

  // Move focus into the menu when it opens.
  useEffect(() => {
    if (!open) return
    menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
  }, [open])

  if (status !== 'success' || workspaces.length === 0) return null

  const current = pickCurrent(workspaces)
  if (!current) return null

  const isCollapsed = collapsed ?? isRailCollapsed()
  const letter = (current.name.trim().charAt(0) || '?').toUpperCase()
  const hasOthers = workspaces.length > 1

  function toggle() {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect()
      setPos({ top: r.bottom + 4, left: r.left })
    }
    setOpen((v) => !v)
  }

  function onMenuKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End']
    if (!keys.includes(e.key)) return
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])
    if (items.length === 0) return
    e.preventDefault()
    const idx = items.indexOf(document.activeElement as HTMLButtonElement)
    let next = idx
    if (e.key === 'ArrowDown') next = (idx + 1) % items.length
    else if (e.key === 'ArrowUp') next = (idx - 1 + items.length) % items.length
    else if (e.key === 'Home') next = 0
    else next = items.length - 1
    items[next]?.focus()
  }

  function choose(id: string) {
    setOpen(false)
    if (id !== current?.id) setActiveWorkspace(id)
  }

  function openPage() {
    setOpen(false)
    openBusinessesPage()
  }

  return (
    <div className="biz-switcher" ref={rootRef}>
      <style>{BUSINESS_CSS}</style>
      <button
        ref={triggerRef}
        type="button"
        className={`biz-switcher-btn${isCollapsed ? ' is-collapsed' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Business: ${current.name}`}
        title={isCollapsed ? current.name : undefined}
        onClick={toggle}
      >
        {isCollapsed ? (
          <span className="biz-switcher-letter" aria-hidden="true">
            {letter}
          </span>
        ) : (
          <>
            <span className="biz-switcher-name">{current.name}</span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M3 4.5l3 3 3-3" />
            </svg>
          </>
        )}
      </button>
      {open && (
        <div
          ref={menuRef}
          className="biz-menu"
          role="menu"
          aria-label="Businesses"
          style={{ top: pos.top, left: pos.left }}
          onKeyDown={onMenuKeyDown}
        >
          {hasOthers &&
            workspaces.map((w) => {
              const isCurrent = w.id === current.id
              return (
                <button
                  key={w.id}
                  type="button"
                  role="menuitem"
                  className={`biz-menu-item${isCurrent ? ' is-current' : ''}`}
                  aria-current={isCurrent ? 'true' : undefined}
                  onClick={() => choose(w.id)}
                >
                  <span className="biz-menu-item-label">{w.name}</span>
                  {isCurrent && <span aria-hidden="true">✓</span>}
                </button>
              )
            })}
          {hasOthers && <div className="biz-menu-sep" role="separator" />}
          {hasOthers && (
            <button type="button" role="menuitem" className="biz-menu-item" onClick={openPage}>
              All businesses
            </button>
          )}
          <button type="button" role="menuitem" className="biz-menu-item" onClick={openPage}>
            New business
          </button>
        </div>
      )}
    </div>
  )
}
